import type {
  AppEvent, AppMeta, ConsumptionMethodRecord, EpisodeRecord, HabitRecord,
  MoneyModelRecord, PlanRecord, PlanStateRecord, PurchaseTemplateRecord,
  RescueModuleStatsRecord, UserSettings,
} from '../core/types.js';
import { uuid } from '../core/ids.js';
import { nowIso } from '../core/time.js';
import { track } from '../analytics/ga.js';

// Payload keys safe to forward to analytics. Allowlist only — never include
// free text, names, notes, or anything identifying. Identifiers (habitId,
// planId, episodeId, sessionId) are deliberately excluded.
// Payload keys safe to forward to analytics. Allowlist only — never include
// free text, names, notes, or anything identifying. Identifiers (habitId,
// planId, episodeId, sessionId) are deliberately excluded.
const GA_SAFE_PAYLOAD_KEYS = new Set<string>([
  'moduleId', 'moduleKind', 'methodKind',
  'plannedDurationSec', 'durationMs',
  'crashed', 'encrypted',
  // Urge / episode close metrics
  'outcome', 'totalWaitedMs', 'modulesCompleted', 'challengeMet',
  'waitedMsBeforeUse', 'intensityStart', 'intensityEnd', 'intensityChange',
  // Usage source (e.g. 'urge_command_center', 'today_screen')
  'source',
]);

// Event types whose `durationMs` should also be mirrored as
// `engagement_time_msec` — a GA-standard metric that aggregates without
// custom-metric registration. Lets dashboards show avg/total time per
// rescue module out of the box.
const GA_ENGAGEMENT_EVENT_TYPES = new Set<string>([
  'rescue_completed', 'rescue_abandoned',
]);

function gaParamsFromPayload(payload: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const k of Object.keys(payload)) {
    if (!GA_SAFE_PAYLOAD_KEYS.has(k)) continue;
    const v = payload[k];
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

export const DB_NAME = 'wean-local-db';
export const DB_VERSION = 4;
export const SCHEMA_VERSION = 3;

const STORES = {
  app_meta: 'app_meta',
  habits: 'habits',
  plans: 'plans',
  plan_states: 'plan_states',
  events: 'events',
  money_models: 'money_models',
  rescue_module_stats: 'rescue_module_stats',
  user_settings: 'user_settings',
  reflections: 'reflections',
  minigame_state: 'minigame_state',
  consumption_methods: 'consumption_methods',
  purchase_templates: 'purchase_templates',
  episodes: 'episodes',
} as const;

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.app_meta)) {
        db.createObjectStore(STORES.app_meta, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.habits)) {
        const s = db.createObjectStore(STORES.habits, { keyPath: 'id' });
        s.createIndex('createdAt', 'createdAt');
        s.createIndex('active', 'active');
      }
      if (!db.objectStoreNames.contains(STORES.plans)) {
        const s = db.createObjectStore(STORES.plans, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('planType', 'planType');
        s.createIndex('active', 'active');
      }
      if (!db.objectStoreNames.contains(STORES.plan_states)) {
        db.createObjectStore(STORES.plan_states, { keyPath: 'planId' });
      }
      if (!db.objectStoreNames.contains(STORES.events)) {
        const s = db.createObjectStore(STORES.events, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('planId', 'planId');
        s.createIndex('type', 'type');
        s.createIndex('occurredAt', 'occurredAt');
        s.createIndex('habitId_occurredAt', ['habitId', 'occurredAt']);
        s.createIndex('createdAt', 'createdAt');
        s.createIndex('episodeId', 'episodeId');
      } else if (req.transaction) {
        const s = req.transaction.objectStore(STORES.events);
        if (!s.indexNames.contains('episodeId')) s.createIndex('episodeId', 'episodeId');
      }
      if (!db.objectStoreNames.contains(STORES.money_models)) {
        db.createObjectStore(STORES.money_models, { keyPath: 'habitId' });
      }
      if (!db.objectStoreNames.contains(STORES.rescue_module_stats)) {
        const s = db.createObjectStore(STORES.rescue_module_stats, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('moduleId', 'moduleId');
        s.createIndex('kind', 'kind');
      }
      if (!db.objectStoreNames.contains(STORES.user_settings)) {
        db.createObjectStore(STORES.user_settings, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.reflections)) {
        const s = db.createObjectStore(STORES.reflections, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('createdAt', 'createdAt');
        s.createIndex('localDate', 'localDate');
      }
      if (!db.objectStoreNames.contains(STORES.minigame_state)) {
        db.createObjectStore(STORES.minigame_state, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.consumption_methods)) {
        const s = db.createObjectStore(STORES.consumption_methods, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('domain', 'domain');
        s.createIndex('active', 'active');
        s.createIndex('habitId_active', ['habitId', 'active']);
      }
      if (!db.objectStoreNames.contains(STORES.episodes)) {
        const s = db.createObjectStore(STORES.episodes, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('kind', 'kind');
        s.createIndex('startedAt', 'startedAt');
        s.createIndex('habitId_endedAt', ['habitId', 'endedAt']);
      }
      if (!db.objectStoreNames.contains(STORES.purchase_templates)) {
        const s = db.createObjectStore(STORES.purchase_templates, { keyPath: 'id' });
        s.createIndex('habitId', 'habitId');
        s.createIndex('methodId', 'methodId');
        s.createIndex('active', 'active');
        s.createIndex('habitId_active', ['habitId', 'active']);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
  return dbPromise;
}

function tx(db: IDBDatabase, names: string[], mode: IDBTransactionMode): IDBTransaction {
  return db.transaction(names, mode);
}

function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

export async function metaGet<K extends keyof AppMeta>(key: K): Promise<AppMeta[K] | undefined> {
  const db = await openDb();
  const r = await reqDone(tx(db, [STORES.app_meta], 'readonly').objectStore(STORES.app_meta).get(key as string));
  return (r as { key: string; value: AppMeta[K] } | undefined)?.value;
}
export async function metaSet<K extends keyof AppMeta>(key: K, value: AppMeta[K]): Promise<void> {
  const db = await openDb();
  await reqDone(tx(db, [STORES.app_meta], 'readwrite').objectStore(STORES.app_meta).put({ key, value, updatedAt: nowIso() }));
}

export async function metaAll(): Promise<Partial<AppMeta>> {
  const db = await openDb();
  const all = await reqDone(tx(db, [STORES.app_meta], 'readonly').objectStore(STORES.app_meta).getAll());
  const out: Record<string, unknown> = {};
  for (const r of all as Array<{ key: string; value: unknown }>) out[r.key] = r.value;
  return out as Partial<AppMeta>;
}

export async function ensureInstall(): Promise<AppMeta> {
  const cur = await metaAll();
  if (!cur.install_id) {
    await metaSet('install_id', uuid());
    await metaSet('created_at', nowIso());
    await metaSet('schema_version', SCHEMA_VERSION);
    await metaSet('app_version', '0.1.0');
  }
  return (await metaAll()) as AppMeta;
}

// Generic helpers
async function putOne<T>(store: string, val: T): Promise<void> {
  const db = await openDb();
  await reqDone(tx(db, [store], 'readwrite').objectStore(store).put(val as unknown as IDBValidKey extends never ? never : object));
}
async function getOne<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb();
  return (await reqDone(tx(db, [store], 'readonly').objectStore(store).get(key))) as T | undefined;
}
async function deleteOne(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDb();
  await reqDone(tx(db, [store], 'readwrite').objectStore(store).delete(key));
}
async function getAll<T>(store: string): Promise<T[]> {
  const db = await openDb();
  return (await reqDone(tx(db, [store], 'readonly').objectStore(store).getAll())) as T[];
}
async function getAllByIndex<T>(store: string, index: string, key: IDBValidKey | IDBKeyRange): Promise<T[]> {
  const db = await openDb();
  return (await reqDone(tx(db, [store], 'readonly').objectStore(store).index(index).getAll(key))) as T[];
}

// Habits
export const habits = {
  put: (h: HabitRecord) => putOne(STORES.habits, h),
  get: (id: string) => getOne<HabitRecord>(STORES.habits, id),
  all: () => getAll<HabitRecord>(STORES.habits),
  delete: (id: string) => deleteOne(STORES.habits, id),
};

// Plans
export const plans = {
  put: (p: PlanRecord) => putOne(STORES.plans, p),
  get: (id: string) => getOne<PlanRecord>(STORES.plans, id),
  byHabit: (habitId: string) => getAllByIndex<PlanRecord>(STORES.plans, 'habitId', habitId),
  all: () => getAll<PlanRecord>(STORES.plans),
  delete: (id: string) => deleteOne(STORES.plans, id),
};

// Plan states
export const planStates = {
  put: (s: PlanStateRecord) => putOne(STORES.plan_states, s),
  get: (planId: string) => getOne<PlanStateRecord>(STORES.plan_states, planId),
  all: () => getAll<PlanStateRecord>(STORES.plan_states),
  delete: (planId: string) => deleteOne(STORES.plan_states, planId),
};

// Events
export const events = {
  put: (e: AppEvent) => putOne(STORES.events, e),
  get: (id: string) => getOne<AppEvent>(STORES.events, id),
  byHabit: (habitId: string) => getAllByIndex<AppEvent>(STORES.events, 'habitId', habitId),
  all: () => getAll<AppEvent>(STORES.events),
  delete: (id: string) => deleteOne(STORES.events, id),
  byHabitInRange: async (habitId: string, fromIso: string, toIso: string) => {
    const all = await getAllByIndex<AppEvent>(STORES.events, 'habitId_occurredAt',
      IDBKeyRange.bound([habitId, fromIso], [habitId, toIso]));
    return all;
  },
};

// Consumption methods
export const consumptionMethods = {
  put: (m: ConsumptionMethodRecord) => putOne(STORES.consumption_methods, m),
  get: (id: string) => getOne<ConsumptionMethodRecord>(STORES.consumption_methods, id),
  byHabit: (habitId: string) =>
    getAllByIndex<ConsumptionMethodRecord>(STORES.consumption_methods, 'habitId', habitId),
  all: () => getAll<ConsumptionMethodRecord>(STORES.consumption_methods),
  delete: (id: string) => deleteOne(STORES.consumption_methods, id),
};

// Purchase templates
export const purchaseTemplates = {
  put: (p: PurchaseTemplateRecord) => putOne(STORES.purchase_templates, p),
  get: (id: string) => getOne<PurchaseTemplateRecord>(STORES.purchase_templates, id),
  byHabit: (habitId: string) =>
    getAllByIndex<PurchaseTemplateRecord>(STORES.purchase_templates, 'habitId', habitId),
  all: () => getAll<PurchaseTemplateRecord>(STORES.purchase_templates),
  delete: (id: string) => deleteOne(STORES.purchase_templates, id),
};

// Episodes
export const episodes = {
  put: (e: EpisodeRecord) => putOne(STORES.episodes, e),
  get: (id: string) => getOne<EpisodeRecord>(STORES.episodes, id),
  byHabit: (habitId: string) => getAllByIndex<EpisodeRecord>(STORES.episodes, 'habitId', habitId),
  all: () => getAll<EpisodeRecord>(STORES.episodes),
  delete: (id: string) => deleteOne(STORES.episodes, id),
  /** Active = endedAt undefined. Returns most recently started open episode for habit, if any. */
  async activeForHabit(habitId: string): Promise<EpisodeRecord | undefined> {
    const all = await getAllByIndex<EpisodeRecord>(STORES.episodes, 'habitId', habitId);
    const open = all.filter((e) => !e.endedAt);
    open.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    return open[0];
  },
};

// Money models
export const moneyModels = {
  put: (m: MoneyModelRecord) => putOne(STORES.money_models, m),
  get: (habitId: string) => getOne<MoneyModelRecord>(STORES.money_models, habitId),
};

// Rescue stats
export const rescueStats = {
  put: (r: RescueModuleStatsRecord) => putOne(STORES.rescue_module_stats, r),
  byHabit: (habitId: string) => getAllByIndex<RescueModuleStatsRecord>(STORES.rescue_module_stats, 'habitId', habitId),
  all: () => getAll<RescueModuleStatsRecord>(STORES.rescue_module_stats),
};

// User settings
export const settings = {
  async get(): Promise<UserSettings> {
    const db = await openDb();
    const r = await reqDone(tx(db, [STORES.user_settings], 'readonly').objectStore(STORES.user_settings).get('default'));
    const def: UserSettings = {
      tone: 'calm', theme: 'auto', reducedMotion: 'off',
      privacyMode: false, notificationsEnabled: false,
      backupRemindersEnabled: false,
      defaultRescueDurationSec: 90, preferredRescueTypes: ['minigame', 'breathing'],
    };
    const stored = (r as { key: string; value: UserSettings } | undefined)?.value;
    return stored ? { ...def, ...stored } : def;
  },
  async set(s: UserSettings) {
    const db = await openDb();
    await reqDone(tx(db, [STORES.user_settings], 'readwrite').objectStore(STORES.user_settings).put({ key: 'default', value: s, updatedAt: nowIso() }));
  },
};

// Minigame state — small KV per module
interface MinigameRecord<T = unknown> { id: string; state: T; updatedAt: string }
export const minigameState = {
  async get<T>(id: string): Promise<T | undefined> {
    const r = await getOne<MinigameRecord<T>>(STORES.minigame_state, id);
    return r?.state;
  },
  async put<T>(id: string, state: T): Promise<void> {
    await putOne(STORES.minigame_state, { id, state, updatedAt: nowIso() } as MinigameRecord<T>);
  },
};

// Reflections
export interface ReflectionRecord {
  id: string;
  habitId: string;
  promptId: string;
  text?: string | undefined;
  chips?: string[] | undefined;
  createdAt: string;
  localDate: string;
}
export const reflections = {
  put: (r: ReflectionRecord) => putOne(STORES.reflections, r),
  byHabit: (habitId: string) => getAllByIndex<ReflectionRecord>(STORES.reflections, 'habitId', habitId),
};

// Bulk export/import helpers
export async function exportAll(): Promise<Record<string, unknown[]>> {
  const out: Record<string, unknown[]> = {};
  for (const name of Object.values(STORES)) {
    out[name] = await getAll<unknown>(name);
  }
  return out;
}

export async function clearAll(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((res, rej) => {
    const t = db.transaction(Object.values(STORES), 'readwrite');
    for (const name of Object.values(STORES)) t.objectStore(name).clear();
    t.oncomplete = () => res();
    t.onerror = () => rej(t.error);
  });
}

export async function importAll(payload: Record<string, unknown[]>, mode: 'replace' | 'merge'): Promise<void> {
  const db = await openDb();
  if (mode === 'replace') await clearAll();
  await new Promise<void>((res, rej) => {
    const t = db.transaction(Object.values(STORES), 'readwrite');
    for (const [name, rows] of Object.entries(payload)) {
      if (!Object.values(STORES).includes(name as typeof STORES[keyof typeof STORES])) continue;
      const s = t.objectStore(name);
      for (const row of rows) s.put(row as object);
    }
    t.oncomplete = () => res();
    t.onerror = () => rej(t.error);
  });
}

export const STORE_NAMES = STORES;

export async function logEvent(e: Omit<AppEvent, 'id' | 'schemaVersion' | 'createdAt' | 'updatedAt' | 'deviceId'>): Promise<AppEvent> {
  const installId = (await metaGet('install_id')) ?? 'unknown';
  const now = nowIso();
  const ev: AppEvent = {
    id: uuid(),
    schemaVersion: SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    deviceId: installId,
    status: 'active',
    ...e,
  };
  await events.put(ev);
  const gaParams = gaParamsFromPayload(ev.payload);
  if (GA_ENGAGEMENT_EVENT_TYPES.has(ev.type) && typeof gaParams['durationMs'] === 'number') {
    gaParams['engagement_time_msec'] = gaParams['durationMs'];
  }
  track(ev.type, gaParams);
  return ev;
}

/** Mark an event as undone. Idempotent — repeated calls keep it undone. */
export async function markEventUndone(eventId: string, reason?: string): Promise<AppEvent | undefined> {
  const ev = await events.get(eventId);
  if (!ev) return undefined;
  if (ev.status === 'undone') return ev;
  const updated: AppEvent = {
    ...ev,
    status: 'undone',
    updatedAt: nowIso(),
    payload: { ...ev.payload, ...(reason ? { undoReason: reason } : {}) },
  };
  await events.put(updated);
  return updated;
}

/** Treat events missing `status` as active for migration safety. */
export function isActiveEvent(ev: AppEvent): boolean {
  return ev.status === undefined || ev.status === 'active';
}
